import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { CreditView } from '@popcart/core/v1';
import { arrowNav, StatusPill } from '../../ui/bits';
import { Fig } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { peso } from '../../lib/format.js';
import { CREDIT_LIMITS, creditSummary, validateCredit } from './lib.js';
import type { OpsRider } from '../queries';

/** A modal on <dialog> (focus trap, Esc and an inert page for free), opened by `open`. */
function Modal({ open, labelledBy, onClose, children }: { open: boolean; labelledBy: string; onClose(): void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="dialog" aria-labelledby={labelledBy} onClose={onClose}>
      {open ? children : null}
    </dialog>
  );
}

/** Pause / Resume Express confirm. Pausing is the red button; resuming is the one yellow one. */
export function PauseDialog({
  open,
  prompt,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  prompt: { title: string; body: string; confirm: string; danger: boolean };
  busy: boolean;
  onConfirm(): void;
  onClose(): void;
}) {
  return (
    <Modal open={open} labelledBy="xp-pause-title" onClose={onClose}>
      <form
        className="stack s24"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm();
        }}
      >
        <h2 id="xp-pause-title" className="t-headline">
          {prompt.title}
        </h2>
        <p className="t-body">{prompt.body}</p>
        <div className="row xp-dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Not now
          </button>
          <button type="submit" className={prompt.danger ? 'btn btn-danger' : 'btn btn-primary'} disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Saving…' : prompt.confirm}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Pick an online, free rider for an unassigned Express run (POST /ops/deliveries/:id/assign). */
export function AssignDialog({
  run,
  riders,
  loading,
  busy,
  onAssign,
  onClose,
}: {
  run: { code: string; shopNames: string[] } | null;
  riders: OpsRider[];
  loading: boolean;
  busy: boolean;
  onAssign(riderUserId: string): void;
  onClose(): void;
}) {
  const [riderId, setRiderId] = useState<string | null>(null);
  useEffect(() => setRiderId(null), [run]);
  const rider = riders.find((r) => r.userId === riderId) ?? null;
  return (
    <Modal open={run !== null} labelledBy="xp-assign-title" onClose={onClose}>
      <form
        className="stack s24"
        onSubmit={(e) => {
          e.preventDefault();
          if (rider) onAssign(rider.userId);
        }}
      >
        <div className="stack s4">
          <h2 id="xp-assign-title" className="t-headline">
            Assign a rider to {run?.code}
          </h2>
          <span className="dv-sub xp-from">
            <Icon name="bag" size="sm" />
            {run?.shopNames.join(' → ')}
          </span>
        </div>
        {loading ? (
          <p className="t-caption muted" role="status">
            Loading riders…
          </p>
        ) : riders.length ? (
          <div role="radiogroup" aria-label="Free rider" className="stack xp-pick" onKeyDown={(e) => arrowNav(e)}>
            {riders.map((r, i) => (
              <button
                key={r.userId}
                type="button"
                role="radio"
                aria-checked={r.userId === riderId}
                tabIndex={r.userId === riderId || (riderId === null && i === 0) ? 0 : -1}
                className={r.userId === riderId ? 'ticket sel' : 'ticket'}
                onClick={() => setRiderId(r.userId)}
              >
                <span className="xp-pick-r">
                  <span className="rd-st" aria-hidden="true" />
                  <b>{r.name}</b>
                  <span className="grow" />
                  <span className="sr-only">Cash on hand </span>
                  <Fig text={peso(r.cashOnHandCentavos)} size="sm" animate={false} />
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="dv-quiet" title="Every online rider is on a run. The sweeper keeps trying every 30 s; you can also free someone up from Riders & dispatch.">
            <Icon name="person" />
            No free riders online
          </div>
        )}
        <div className="row xp-dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={!rider || busy} aria-busy={busy || undefined}>
            <Icon name="pin" />
            {busy ? 'Assigning…' : rider ? `Assign ${rider.name}` : 'Assign rider'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Goodwill credit for one order (POST /ops/express/credits, reason goodwill). Amount in pesos,
 * a required note; after it's issued the dialog shows the created credit.
 */
export function CreditDialog({
  order,
  busy,
  created,
  error,
  onIssue,
  onClose,
}: {
  order: { orderId: string; code: string; customerFirstName: string } | null;
  busy: boolean;
  created: CreditView | null;
  error: string | null;
  onIssue(v: { amountCentavos: number; note: string }): void;
  onClose(): void;
}) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; note?: string }>({});
  const amountRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setAmount('');
    setNote('');
    setErrors({});
  }, [order?.orderId]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = validateCredit({ amount, note });
    if (!r.ok) {
      setErrors(r.errors);
      (r.errors.amount ? amountRef : noteRef).current?.focus();
      return;
    }
    setErrors({});
    onIssue(r.value);
  };

  return (
    <Modal open={order !== null} labelledBy="xp-credit-title" onClose={onClose}>
      {created ? (
        <div className="stack s24">
          <div className="stack s4">
            <h2 id="xp-credit-title" className="t-headline">
              Credit issued
            </h2>
            <span className="t-caption muted">{order?.customerFirstName} sees it in Me → Credits and at checkout.</span>
          </div>
          <div className="xp-credit-made" role="status">
            <Fig text={peso(created.amountCentavos)} size="lg" />
            <span className="t-label">{creditSummary(created, order?.code ?? created.sourceOrderCode)}</span>
            <StatusPill tone="info">Goodwill</StatusPill>
          </div>
          <div className="row xp-dialog-actions">
            <button type="button" className="btn btn-primary" onClick={onClose} autoFocus>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="stack s24" onSubmit={submit} noValidate>
          <div className="stack s4">
            <h2 id="xp-credit-title" className="t-headline">
              Goodwill credit for {order?.code}
            </h2>
            <span className="t-caption muted">
              For {order?.customerFirstName}. It pays the delivery fee and Express surcharge of a later order, never goods or cash.
            </span>
          </div>
          <div className="field">
            <label htmlFor="xp-credit-amount">Amount</label>
            <div className="input-affix" data-invalid={errors.amount ? 'true' : undefined}>
              <span aria-hidden="true" className="t-mono">
                ₱
              </span>
              <input
                id="xp-credit-amount"
                ref={amountRef}
                inputMode="decimal"
                autoComplete="off"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-invalid={errors.amount ? true : undefined}
                aria-describedby={errors.amount ? 'xp-credit-amount-err' : 'xp-credit-amount-help'}
              />
            </div>
            <span id="xp-credit-amount-help" className="help">
              {peso(CREDIT_LIMITS.minCentavos)} to {peso(CREDIT_LIMITS.maxCentavos)}.
            </span>
            {errors.amount ? (
              <span id="xp-credit-amount-err" className="pill st-error err-pill">
                {errors.amount}
              </span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="xp-credit-note">Note (kept in the audit log)</label>
            <textarea
              id="xp-credit-note"
              ref={noteRef}
              className="input"
              value={note}
              maxLength={CREDIT_LIMITS.noteMax}
              onChange={(e) => setNote(e.target.value)}
              aria-invalid={errors.note ? true : undefined}
              aria-describedby={errors.note ? 'xp-credit-note-err' : undefined}
            />
            {errors.note ? (
              <span id="xp-credit-note-err" className="pill st-error err-pill">
                {errors.note}
              </span>
            ) : null}
          </div>
          {error ? (
            <p className="pill st-error err-pill" role="alert">
              {error}
            </p>
          ) : null}
          <div className="row xp-dialog-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
              {busy ? 'Issuing…' : 'Issue credit'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
